"use client";

import Script from "next/script";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CONSENT_OPEN_EVENT,
  readConsent,
  writeConsent,
  type ConsentChoice,
} from "@/lib/consent";

interface CookieConsentProps {
  pixelId?: string;
  brevoKey?: string;
  apolloAppId?: string;
}

export function CookieConsent({
  pixelId,
  brevoKey,
  apolloAppId,
}: CookieConsentProps) {
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setChoice(readConsent());
    setReady(true);
    const reopen = () => setOpen(true);
    window.addEventListener(CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
  }, []);

  const decide = (next: ConsentChoice) => {
    writeConsent(next);
    setChoice(next);
    setOpen(false);
  };

  const showBanner = ready && (choice === null || open);

  return (
    <>
      {choice === "accepted" && (
        <>
          {brevoKey && (
            <>
              <Script
                src="https://cdn.brevo.com/js/sdk-loader.js"
                strategy="afterInteractive"
              />
              <Script id="brevo-init" strategy="afterInteractive">
                {`window.Brevo=window.Brevo||[];Brevo.push(["init",{"client_key":"${brevoKey}"}]);`}
              </Script>
            </>
          )}
          {pixelId && (
            <Script id="fb-pixel" strategy="afterInteractive">
              {`
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${pixelId}');
                fbq('track', 'PageView');
              `}
            </Script>
          )}
          {apolloAppId && (
            <Script id="apollo-pixel" strategy="afterInteractive">
              {`
                function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");
                o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,
                o.onload=function(){window.trackingFunctions.onLoad({appId:"${apolloAppId}"})},
                document.head.appendChild(o)}initApollo();
              `}
            </Script>
          )}
        </>
      )}

      {showBanner && (
        <div
          role="dialog"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-50 border-t-2 border-muted-dark bg-background p-4 shadow-lg"
        >
          <div className="container mx-auto flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <p className="text-sm text-muted-light">
              We use cookies for analytics and marketing to improve your
              experience. You can accept or reject non-essential cookies. See our{" "}
              <a href="/policies#cookie" className="text-primary underline">
                Cookie Policy
              </a>
              .
            </p>
            <div className="flex shrink-0 gap-3">
              <Button variant="outline" onClick={() => decide("rejected")}>
                Reject
              </Button>
              <Button onClick={() => decide("accepted")}>Accept</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
